'use client';

import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Search, Sparkles, ChevronRight } from 'lucide-react';
import {
  PolicyTemplate,
  PolicyCategory,
  policyTemplates,
  categoryInfo,
  searchTemplates,
  getTemplatesByCategory,
  ParsedPolicyState,
  parseCedarToState,
} from './policyTemplates';

export interface PolicyTemplateGalleryProps {
  onSelectTemplate: (template: PolicyTemplate, parsedState: Partial<ParsedPolicyState>) => void;
  compact?: boolean;
}

export function PolicyTemplateGallery({ onSelectTemplate, compact = false }: PolicyTemplateGalleryProps) {
  const [selectedCategory, setSelectedCategory] = useState<PolicyCategory | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [previewTemplate, setPreviewTemplate] = useState<PolicyTemplate | null>(null);

  const filteredTemplates = useMemo(() => {
    let templates = policyTemplates;

    if (searchQuery) {
      templates = searchTemplates(searchQuery);
    } else if (selectedCategory !== 'all') {
      templates = getTemplatesByCategory(selectedCategory);
    }

    return templates;
  }, [selectedCategory, searchQuery]);

  const handleUseTemplate = (template: PolicyTemplate) => {
    const parsedState = parseCedarToState(template.cedarPolicy);
    onSelectTemplate(template, parsedState);
    setPreviewTemplate(null);
  };

  const categories: (PolicyCategory | 'all')[] = ['all', 'access', 'role-based', 'ownership', 'data-protection'];

  if (compact) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-amber-500" />
          <span className="text-sm font-medium text-slate-700">Quick Templates</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {policyTemplates.slice(0, 5).map(template => (
            <Button
              key={template.id}
              variant="outline"
              size="sm"
              className="h-7 text-xs"
              onClick={() => handleUseTemplate(template)}
            >
              <template.icon className="h-3 w-3 mr-1" />
              {template.name}
            </Button>
          ))}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-slate-500"
            onClick={() => setPreviewTemplate(policyTemplates[0])}
          >
            View all...
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Sparkles className="h-5 w-5 text-amber-500" />
              Policy Templates
            </CardTitle>
            <CardDescription>
              Start with a pre-built template and customize as needed.
            </CardDescription>
          </div>
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Category Tabs */}
        <Tabs value={selectedCategory} onValueChange={(v) => setSelectedCategory(v as PolicyCategory | 'all')}>
          <TabsList className="h-9 bg-slate-100/80">
            {categories.map(cat => (
              <TabsTrigger key={cat} value={cat} className="text-xs px-3">
                {cat === 'all' ? 'All' : categoryInfo[cat].label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Template Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filteredTemplates.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              onPreview={() => setPreviewTemplate(template)}
              onUse={() => handleUseTemplate(template)}
            />
          ))}
        </div>

        {filteredTemplates.length === 0 && (
          <div className="text-center py-8 text-slate-500">
            No templates found matching your search.
          </div>
        )}
      </CardContent>

      {/* Preview Dialog */}
      <Dialog open={!!previewTemplate} onOpenChange={() => setPreviewTemplate(null)}>
        <DialogContent className="max-w-2xl">
          {previewTemplate && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <previewTemplate.icon className="h-5 w-5 text-indigo-600" />
                  {previewTemplate.name}
                </DialogTitle>
                <DialogDescription>{previewTemplate.description}</DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="flex flex-wrap gap-1">
                  <Badge variant="outline" className="text-xs">
                    {categoryInfo[previewTemplate.category].label}
                  </Badge>
                  {previewTemplate.tags.map(tag => (
                    <Badge key={tag} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>

                <div>
                  <h4 className="text-sm font-medium mb-2">Policy Code</h4>
                  <pre className="p-4 rounded-md bg-slate-900 text-slate-50 font-mono text-sm overflow-x-auto">
                    {previewTemplate.cedarPolicy}
                  </pre>
                </div>

                {previewTemplate.placeholders && previewTemplate.placeholders.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-2">Customizable Parameters</h4>
                    <div className="space-y-2">
                      {previewTemplate.placeholders.map(ph => (
                        <div key={ph.key} className="flex items-center gap-2 text-sm">
                          <Badge variant="outline" className="font-mono">{ph.key}</Badge>
                          <span className="text-slate-500">{ph.label}</span>
                          {ph.options && (
                            <span className="text-slate-400 text-xs">
                              Options: {ph.options.join(', ')}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setPreviewTemplate(null)}>
                  Cancel
                </Button>
                <Button onClick={() => handleUseTemplate(previewTemplate)}>
                  Use This Template
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

interface TemplateCardProps {
  template: PolicyTemplate;
  onPreview: () => void;
  onUse: () => void;
}

function TemplateCard({ template, onPreview, onUse }: TemplateCardProps) {
  const Icon = template.icon;

  return (
    <div
      className="group relative p-4 border rounded-lg bg-white hover:border-indigo-300 hover:shadow-sm transition-all cursor-pointer"
      onClick={onPreview}
    >
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-md bg-slate-100 group-hover:bg-indigo-50 transition-colors">
          <Icon className="h-4 w-4 text-slate-600 group-hover:text-indigo-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm text-slate-900 truncate">{template.name}</h4>
          <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{template.description}</p>
          <div className="flex items-center gap-1 mt-2">
            <Badge
              variant="outline"
              className={`text-[10px] px-1.5 py-0 ${
                template.category === 'data-protection'
                  ? 'border-red-200 text-red-600 bg-red-50'
                  : template.category === 'ownership'
                  ? 'border-amber-200 text-amber-600 bg-amber-50'
                  : template.category === 'role-based'
                  ? 'border-purple-200 text-purple-600 bg-purple-50'
                  : 'border-blue-200 text-blue-600 bg-blue-50'
              }`}
            >
              {categoryInfo[template.category].label}
            </Badge>
          </div>
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="absolute top-2 right-2 h-7 px-2 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={(e) => {
          e.stopPropagation();
          onUse();
        }}
      >
        Use
        <ChevronRight className="h-3 w-3 ml-1" />
      </Button>
    </div>
  );
}
